import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/services/auth';
import { Button, Text } from '@/components/atoms';
import { Stack } from '@/components/layout';
import { FormField } from '@/components/composed';
import { useRequestPasswordResetWithPinGraphQL, useRequestLoginLinkGraphQL } from '@/hooks/graphql/useLoginTokensGraphQL';
import logger from '@/utils/logger';

type SentType = 'password_reset' | 'login_link' | null;

export function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState<SentType>(null);
  const isVerified = searchParams.get('verified') === 'true';

  const { requestReset, loading: resetLoading } = useRequestPasswordResetWithPinGraphQL();
  const { requestLink, loading: linkLoading } = useRequestLoginLinkGraphQL();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      // Redirect to returnUrl if present, otherwise home
      // Feature: 647-invite-registration US4 - returnUrl support
      const returnUrl = searchParams.get('returnUrl');
      if (returnUrl) {
        // Validate returnUrl is a relative path (security: prevent open redirect)
        const decoded = decodeURIComponent(returnUrl);
        if (decoded.startsWith('/') && !decoded.startsWith('//')) {
          navigate(decoded);
          return;
        }
      }
      navigate('/');
    } catch (err: unknown) {
      const errorMessage = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || 'Login failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setError('');
    try {
      await requestReset(email);
      setLinkSent('password_reset');
    } catch (err: unknown) {
      logger.error('LoginForm', 'Password reset request failed:', err);
      setError('Failed to send reset email. Please try again.');
    }
  };

  const handleLoginLink = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setError('');
    try {
      await requestLink(email);
      setLinkSent('login_link');
    } catch (err: unknown) {
      logger.error('LoginForm', 'Login link request failed:', err);
      setError('Failed to send login link. Please try again.');
    }
  };

  if (linkSent) {
    const isPasswordReset = linkSent === 'password_reset';
    return (
      <div className="mt-8">
        <Stack direction="vertical" spacing={4}>
          <div className="bg-surface-DEFAULT rounded-lg p-6">
            <Text className="font-medium mb-2">
              {isPasswordReset ? 'Check Your Email' : 'Login Link Sent'}
            </Text>
            <Text className="mb-4 text-content-secondary">
              If an account exists for <strong>{email}</strong>, you&apos;ll receive an email with a link and a <strong>6-digit PIN</strong>.
            </Text>
            <Text className="text-sm text-content-tertiary">
              {isPasswordReset
                ? 'Click the link and enter the PIN to reset your password.'
                : 'Click the link and enter the PIN to log in.'}
            </Text>
          </div>
          <button
            type="button"
            onClick={() => setLinkSent(null)}
            className="text-primary hover:underline text-sm"
          >
            ← Back to login
          </button>
        </Stack>
      </div>
    );
  }

  const isActionLoading = resetLoading || linkLoading;

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      <Stack direction="vertical" spacing={4}>
        {isVerified && (
          <div className="text-success bg-success/10 border border-success/30 rounded p-3">
            ✓ Email verified successfully! Please log in to continue.
          </div>
        )}
        {error && (
          <Text className="text-danger bg-danger/10 border border-danger/30 rounded p-3">
            {error}
          </Text>
        )}
        <FormField
          id="email"
          label="Email"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />
        <FormField
          id="password"
          label="Password"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <Button type="submit" disabled={loading || isActionLoading} loading={loading} variant="primary">
          {loading ? 'Logging in...' : 'Login'}
        </Button>
        <div className="text-center space-y-2">
          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={isActionLoading}
            className="text-sm text-primary hover:underline block w-full disabled:opacity-50"
          >
            {resetLoading ? 'Sending...' : 'Forgot your password?'}
          </button>
          <button
            type="button"
            onClick={handleLoginLink}
            disabled={isActionLoading}
            className="text-sm text-content-secondary hover:text-primary hover:underline disabled:opacity-50"
          >
            {linkLoading ? 'Sending...' : 'Sign in with email link (no password)'}
          </button>
        </div>
      </Stack>
    </form>
  );
}
